import React from 'react';
import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import ConditionalRender
  from '@avtechsoftware/react-component-library/src/components/common/ConditionalRender/ConditionalRender';
import BooleanChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import RaaExtremeTempFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/RaaExtremeTempFaultChannel';
import IfElseConditionalRender
  from '@avtechsoftware/react-component-library/src/components/common/ConditionalRender/IfElseConditionalRender/IfElseConditionalRender';
import PropTypes from 'prop-types';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import AppTypes from '@avtechsoftware/ra-javascript-utilities/src/types/AppTypes';
import { useSelector } from 'react-redux';
import IntegerAsBooleanChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/IntegerAsBooleanChannel/IntegerAsBooleanChannel';
import { RaaRoles } from '../../../../utility/SecurityRules';
import styles from '../../AlertDetailsPage/AlertDetailsCard/AlertDetailsCard.module.css';
import AlertLastReadingSuspended
  from '../../AlertDetailsPage/AlertDetailsCard/AlertLastReading/AlertLastReadingSuspended/AlertLastReadingSuspended';
import AlertLastReading from '../../AlertDetailsPage/AlertDetailsCard/AlertLastReading/AlertLastReading';
import HasRolePrivileges from '../../../highorder/HasRolePrivileges';
import { selectSingleAlert } from '../../../../redux/slices/singleAlertSlice/singleAlertSlice';
import useFavoriteSingleAlert from '../../AlertDetailsPage/useFavoriteSingleAlert';
import ChannelValueWithUnitsFromChannelKeyObject
  from '../../../common/ChannelValueWithUnitsFromChannelKeyObject/ChannelValueWithUnitsFromChannelKeyObject';
import AlertGraph from '../../../common/AlertGraph/AlertGraph';
import { getAlertSource, getMaxComponentLabel } from '../../../../utility/alertDisplayUtils';
import { selectByUuidOrMacAddress, selectSensorMap } from '../../../../redux/slices/deviceList/deviceListSlice';

function AlertReading(props) {
  const {
    acknowledgeAlert,
  } = props;

  const { toggleFavoriteAlert } = useFavoriteSingleAlert();

  const alert = useSelector(selectSingleAlert);
  const sensorMap = useSelector(selectSensorMap);
  const channelGroupList = sensorMap[alert.channelKey.macAddress];
  const device = useSelector(
    (state) => selectByUuidOrMacAddress(state, alert.channelKey.macAddress),
  );
  const isRAMAXDevice = device && device.type === 'RAMAX';
  let alertSourceParts = null;
  if (isRAMAXDevice && channelGroupList && channelGroupList.length > 0) {
    const maxComponentLabel = getMaxComponentLabel(channelGroupList, alert.channelKey);
    const sourceString = getAlertSource(alert, alert.channel, true, maxComponentLabel);
    alertSourceParts = sourceString.split(' | ').map((part) => part.trim());
  }

  const channel = ChannelService.getChannelGroupChannelByAppType(
    alert.channelKey.channelGroupType,
    alert.channelKey.channelIndex,
    alert.channelKey.channelType,
    AppTypes.CLOUD,
  );

  const getDeviceHref = () => {
    const deviceType = device && device.type ? String(device.type).toUpperCase() : '';

    if (deviceType === 'VIRT') {
      return '/sensors/monitor360';
    }

    if (deviceType === 'CONNECT') {
      return '/links';
    }

    return `/s/devices/${alert.channel.deviceId}`;
  };

  return (
    <>
      <div
        id="alert-summary"
        className={`card px-5 py-4 ${styles['gtmd-alert-summary']} ${alert.triggeredAlertEvent.triggerTimestamp && !alert.isDeviceAssignmentSuspended ? styles['gtmd-alert-triggered'] : ''}`}
      >
        <div className={`mb-1 ${styles.detailsCardHeader}`}>
          <div className="justify-content-center">
            <h5 className="mb-0" style={{ display: 'inline-block' }}>
              <button
                type="button"
                className={`${styles.detailsFavoriteStar} mr-2`}
                onClick={() => toggleFavoriteAlert(alert.id)}
              >
                <Icon
                  icon="star"
                  title="Click on the star to pin or unpin activeAlert."
                  className={`${alert.isFavorite ? styles['gtmd-favorite-alert'] : 'text-muted'} text-center`}
                />
              </button>

              <a href={getDeviceHref()}>
                <span>
                  {alert.channel.deviceName}
                </span>
              </a>
              {' » '}
              <span>
                {isRAMAXDevice ? alertSourceParts[1] : alert.channel.groupName}
              </span>
              {' » '}
              {isRAMAXDevice && (
              <>
                <span>{alertSourceParts[2]}</span>
                {' » '}
              </>
              )}
              <span>
                {alert.channel.name}
              </span>
            </h5>

          </div>
        </div>

        <hr />

        <div className="row">
          <div className="col-md-12 col-lg-3">
            <div className="row mb-2" style={{ minHeight: '31px' }}>
              <div className="col">
                {!alert.isAlertDeactivated && !alert.isDeviceAssignmentSuspended && (
                  <>
                    <HasRolePrivileges targetRoleList={[RaaRoles.MANAGER, RaaRoles.ADMIN]}>
                      <ConditionalRender show={
                        (alert.triggeredAlertEvent.triggerTimestamp && !alert.isAcknowledged)
                      }
                      >
                        <button
                          type="button"
                          className={`btn btn-outline-dark btn-sm ${styles['acknowledge-button']}`}
                          onClick={acknowledgeAlert}
                        >
                          <Icon icon="bell-slash" className="mx-1" />
                          Acknowledge
                        </button>
                      </ConditionalRender>
                    </HasRolePrivileges>

                    <ConditionalRender show={alert.isAcknowledged}>
                      <p>
                        <Icon icon="bell-slash" className="mx-1" />
                        Acknowledged by
                        {' '}
                        <b>
                          <span>
                            {alert.triggeredAlertEvent.acknowledgementUser
                            && alert.triggeredAlertEvent.acknowledgementUser.userFullName}
                          </span>
                        </b>
                        {' '}
                        on
                        {' '}
                        <span>
                          {alert.triggeredAlertEvent.acknowledgementTimestamp}
                        </span>
                      </p>
                    </ConditionalRender>
                  </>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col">
                <IfElseConditionalRender
                  conditionalBool={alert.isDeviceAssignmentSuspended || alert.isAlertDeactivated}
                  TrueComponent={AlertLastReadingSuspended}
                  FalseComponent={AlertLastReading}
                  falseProps={{ activeAlert: alert }}
                  trueProps={{ suspensionTypeString: alert.isDeviceAssignmentSuspended ? 'Device' : 'Alert' }}
                />
              </div>
            </div>

            <div className="row">
              <div className="col">
                <div className={`card ${styles['gtmd-alert-summary-infobox']}`}>
                  <div className={styles['infobox-label']}>Threshold</div>
                  <div className={`${styles['infobox-value']}`}>
                    <ConditionalRender show={(!(channel instanceof BooleanChannel)
                        && !(channel instanceof IntegerAsBooleanChannel)
                        && !(channel instanceof RaaExtremeTempFaultChannel))}
                    >
                      {alert.triggerCondition.operator}
                    </ConditionalRender>
                    {' '}
                    <>
                      {channel instanceof RaaExtremeTempFaultChannel
                      || channel instanceof IntegerAsBooleanChannel
                        ? alert.triggerCondition.formattedCondition
                        : (
                          <ChannelValueWithUnitsFromChannelKeyObject
                            channelKey={alert.channelKey}
                            customUnits={alert.channel.unit}
                            value={alert.triggerCondition.value}
                          />
                        )}
                    </>
                  </div>
                  <div className={styles['infobox-subvalue']}>
                    Clears on
                    {' '}
                    <ConditionalRender
                      show={(!(channel instanceof BooleanChannel)
                            && !(channel instanceof IntegerAsBooleanChannel)
                            && !(channel instanceof RaaExtremeTempFaultChannel))}
                    >
                      {alert.clearCondition.operator}
                    </ConditionalRender>
                    {' '}
                    {channel instanceof RaaExtremeTempFaultChannel
                        || channel instanceof IntegerAsBooleanChannel
                      ? alert.clearCondition.formattedCondition
                      : (
                        <ChannelValueWithUnitsFromChannelKeyObject
                          channelKey={alert.channelKey}
                          customUnits={alert.channel.unit}
                          value={alert.clearCondition.value}
                        />
                      )}
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="col-md-12 col-lg-9">
            {' '}
            <AlertGraph />
            {' '}
          </div>

        </div>
      </div>
      <ConditionalRender show={alert.isDeviceAssignmentSuspended || alert.isAlertDeactivated}>
        <div className="alert alert-warning">
          <div className="float-left">
            <Icon icon="triangle-exclamation" />
          </div>
          <div style={{ marginLeft: '1.5em' }}>
            {alert.isAlertDeactivated ? 'This' : 'The device for this'}
            {' '}
            alert has been suspended because you’ve reached your account’s
            {' '}
            {alert.isAlertDeactivated ? 'alert' : 'device'}
            {' '}
            limit.
            This alert will not trigger or send any notifications. To reactivate your suspended
            {' '}
            {alert.isAlertDeactivated ? 'alerts' : 'devices'}
            ,
            {' '}
            <a href="/s/subscription/billing/change-plan" target="_blank">upgrade</a>
            {' '}
            your account.
            {' '}
            <a
              className="btn btn-sm btn-light border-info py-0"
              href="/s/subscription/billing/change-plan"
              target="_blank"
              style={{ lineHeight: '1' }}
            >
              Upgrade Now
            </a>
          </div>
        </div>
      </ConditionalRender>
    </>
  );
}

AlertReading.propTypes = {
  acknowledgeAlert: PropTypes.func.isRequired,
};

export { AlertReading };
export default AlertReading;
